import { useState } from "react";
import { toast } from "sonner";
import { Package, CheckCircle2, Truck, Home, Search, ExternalLink, AlertCircle } from "lucide-react";

import Header from "../components/Header";
import Footer from "../components/Footer";
import FloatingButtons from "../components/FloatingButtons";
import PageBanner from "../components/PageBanner";
import Seo from "../components/Seo";
import { Input } from "../components/ui/input";
import { useZeoContent } from "../lib/ZeoContentContext";

const STEP_ICONS = { pending: CheckCircle2, preparing: Package, ready: Truck, completed: Home };

// Bilinen kargo firmalarının herkese açık takip sayfası — firma adı serbest
// metin olduğu için basit içerir-eşleşmesiyle bulunur; eşleşme yoksa sadece
// takip numarası gösterilir, uydurma bir link üretilmez.
const CARGO_TRACK_URLS = [
  { match: /yurtiçi/i, url: (code) => `https://www.yurticikargo.com/tr/online-servisler/gonderi-sorgula?code=${encodeURIComponent(code)}` },
  { match: /aras/i, url: (code) => `https://kargotakip.araskargo.com.tr/mainpage.aspx?code=${encodeURIComponent(code)}` },
  { match: /mng/i, url: (code) => `https://www.mngkargo.com.tr/gonderitakip?takipNo=${encodeURIComponent(code)}` },
  { match: /ptt/i, url: (code) => `https://gonderitakip.ptt.gov.tr/Track/Verify?q=${encodeURIComponent(code)}` },
  { match: /sürat|surat/i, url: (code) => `https://www.suratkargo.com.tr/KargoTakip?TakipNo=${encodeURIComponent(code)}` },
];

function cargoTrackUrl(company, code) {
  if (!company || !code) return null;
  const found = CARGO_TRACK_URLS.find((c) => c.match.test(company));
  return found ? found.url(code) : null;
}

export default function OrderTracking() {
  const { business } = useZeoContent();
  const [orderNo, setOrderNo] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [order, setOrder] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!orderNo.trim() || !phone.trim()) return;
    setLoading(true);
    setError("");
    setOrder(null);
    try {
      const params = new URLSearchParams({ order_id: orderNo.trim().replace(/^#/, ""), phone: phone.trim() });
      const res = await fetch(`/api/order-status.php?${params.toString()}`);
      const data = await res.json().catch(() => null);
      if (!res.ok || data?.status !== "success") {
        setError(data?.message || "Sipariş bulunamadı. Sipariş numarası ve telefon numarasını kontrol edin.");
        return;
      }
      setOrder(data.order);
    } catch {
      setError("Bağlantı sorunu — lütfen tekrar deneyin.");
      toast.error("Sipariş sorgulanamadı", { description: "Lütfen internet bağlantınızı kontrol edip tekrar deneyin." });
    } finally {
      setLoading(false);
    }
  };

  const trackUrl = order ? cargoTrackUrl(order.cargo_company, order.cargo_tracking_number) : null;

  return (
    <div className="min-h-screen bg-white text-[#222222]" data-testid="order-tracking-page">
      <Seo title="Sipariş Takibi" noindex />
      <Header />

      <PageBanner
        eyebrow="Yardım"
        title="Sipariş Takibi"
        subtitle="Sipariş numaranız ve telefon numaranızla siparişinizin anlık durumunu görüntüleyin."
        breadcrumb={[{ label: "Sipariş Takibi" }]}
      />

      <section className="py-16 md:py-20 bg-white">
        <div className="frida-container max-w-3xl mx-auto">
          <form
            onSubmit={handleSubmit}
            className="bg-[#F8F8F8] rounded-3xl p-7 md:p-10 grid grid-cols-1 sm:grid-cols-2 gap-5"
            data-testid="order-tracking-form"
          >
            <div>
              <label className="text-sm font-medium text-[#222222] mb-2 block">Sipariş Numarası</label>
              <Input
                required
                value={orderNo}
                onChange={(e) => setOrderNo(e.target.value)}
                placeholder="Örn. 1245"
                className="h-12 rounded-xl border-[#EAEAEA] bg-white focus-visible:ring-[#A85C34]"
                data-testid="order-tracking-number-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-[#222222] mb-2 block">Telefon Numarası</label>
              <Input
                required
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="0532 000 00 00"
                className="h-12 rounded-xl border-[#EAEAEA] bg-white focus-visible:ring-[#A85C34]"
                data-testid="order-tracking-phone-input"
              />
            </div>
            <div className="sm:col-span-2">
              <button
                type="submit"
                disabled={loading}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#A85C34] hover:bg-[#8B4726] text-white rounded-full font-medium transition-colors active:scale-95 disabled:opacity-60"
                data-testid="order-tracking-submit"
              >
                <Search className="w-4 h-4" />
                {loading ? "Sorgulanıyor..." : "Siparişi Sorgula"}
              </button>
            </div>
          </form>

          {error && (
            <div className="mt-6 flex items-center gap-3 px-5 py-4 rounded-2xl bg-red-50 border border-red-100 text-sm text-red-700" data-testid="order-tracking-error">
              <AlertCircle className="w-4 h-4 shrink-0" />
              {error}
            </div>
          )}

          {order && (
            <div className="mt-10 bg-white border border-[#EAEAEA] rounded-3xl p-7 md:p-10" data-testid="order-tracking-result">
              <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
                <h3 className="font-serif text-2xl text-[#222222]">Sipariş #{order.order_id}</h3>
                <span className="px-4 py-1.5 rounded-full bg-[#A85C34]/10 text-[#A85C34] text-sm font-medium">
                  {order.status_label}
                </span>
              </div>

              {order.status === "cancelled" ? (
                <p className="text-sm text-[#888888]">Bu sipariş iptal edilmiştir.</p>
              ) : (
                <div className="relative">
                  <div className="absolute left-5 top-5 bottom-5 w-0.5 bg-[#EAEAEA]" />
                  <div className="space-y-8">
                    {order.steps.map((s, i) => {
                      const Icon = STEP_ICONS[s.key] ?? Package;
                      const done = order.step_index !== null && i <= order.step_index;
                      return (
                        <div key={s.key} className="relative flex items-start gap-5">
                          <div
                            className={`relative z-10 w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                              done ? "bg-[#A85C34] text-white" : "bg-[#F8F8F8] text-[#888888] border border-[#EAEAEA]"
                            }`}
                          >
                            <Icon className="w-4 h-4" />
                          </div>
                          <div>
                            <div className={`font-medium ${done ? "text-[#222222]" : "text-[#888888]"}`}>{s.label}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {order.cargo_company && (
                <div className="mt-8 pt-6 border-t border-[#EAEAEA]" data-testid="order-tracking-cargo">
                  <p className="text-xs uppercase tracking-wide text-[#888888] mb-2">Kargo Bilgisi</p>
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                      <p className="font-medium text-[#222222]">{order.cargo_company}</p>
                      {order.cargo_tracking_number && (
                        <p className="text-sm text-[#888888]">Takip No: {order.cargo_tracking_number}</p>
                      )}
                    </div>
                    {trackUrl && (
                      <a
                        href={trackUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-sm font-medium text-[#A85C34] hover:underline"
                      >
                        Kargo Firmasında Takip Et <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    )}
                  </div>
                </div>
              )}

              {order.delivery_address && (
                <div className="mt-6 pt-6 border-t border-[#EAEAEA] text-sm text-[#888888]">
                  <p className="text-xs uppercase tracking-wide mb-1.5">Teslimat Adresi</p>
                  <p className="text-[#222222]">
                    {order.delivery_address}
                    {order.delivery_ilce ? `, ${order.delivery_ilce}` : ""}
                    {order.delivery_il ? ` / ${order.delivery_il}` : ""}
                  </p>
                </div>
              )}
            </div>
          )}

          <p className="text-center text-sm text-[#888888] mt-10">
            Sorularınız için{" "}
            <a href={`tel:${business.phoneRaw}`} className="text-[#A85C34] font-medium hover:underline">
              {business.phone}
            </a>{" "}
            numaramızdan bize ulaşabilirsiniz.
          </p>
        </div>
      </section>

      <Footer />
      <FloatingButtons />
    </div>
  );
}
